/**
 * Document scroll progress 0–1.
 * Uses max of layout metrics so scrollHeight − viewport matches how far the page can scroll.
 */
export function getDocumentScrollProgress() {
  if (typeof window === 'undefined') return 0;

  const docEl = document.documentElement;
  const body = document.body;
  const se = document.scrollingElement || docEl;

  const scrollTop =
    window.scrollY ??
    window.pageYOffset ??
    se.scrollTop ??
    docEl.scrollTop ??
    body?.scrollTop ??
    0;

  const scrollHeight = Math.max(
    body?.scrollHeight ?? 0,
    docEl.scrollHeight,
    body?.offsetHeight ?? 0,
    docEl.offsetHeight,
    se.scrollHeight ?? 0
  );

  const vh = window.innerHeight || docEl.clientHeight || body?.clientHeight || 0;
  const maxScroll = scrollHeight - vh;
  if (maxScroll <= 0) return 0;
  return Math.min(1, Math.max(0, scrollTop / maxScroll));
}

let scrollToTargetRafId = null;

function getScrollY() {
  return (
    window.pageYOffset ??
    document.documentElement.scrollTop ??
    document.body.scrollTop ??
    0
  );
}

/** Set vertical scroll on all roots WebKit / legacy may use (instant). */
function setScrollY(y) {
  const y2 = Math.max(0, Math.round(y));
  window.scrollTo(0, y2);
  const se = document.scrollingElement || document.documentElement;
  se.scrollTop = y2;
  if (document.body) document.body.scrollTop = y2;
}

/** Instant scroll to top — use on route change (covers iOS / legacy browsers). */
export function scrollPageTopInstant() {
  if (typeof window === 'undefined') return;
  setScrollY(0);
}

function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

function getMaxScrollY() {
  const docEl = document.documentElement;
  const body = document.body;
  const se = document.scrollingElement || docEl;
  const scrollHeight = Math.max(
    body?.scrollHeight ?? 0,
    docEl.scrollHeight,
    body?.offsetHeight ?? 0,
    docEl.offsetHeight,
    se.scrollHeight ?? 0
  );
  const vh = window.innerHeight || docEl.clientHeight || body?.clientHeight || 0;
  return Math.max(0, scrollHeight - vh);
}

/**
 * Animated scroll to a document Y offset. Uses rAF + easing — native `behavior: 'smooth'` is flaky on Safari / iOS.
 * Respects `prefers-reduced-motion` (instant jump).
 */
export function scrollToYOffsetSmooth(targetY) {
  if (typeof window === 'undefined') return;

  const prefersReduced =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const end = Math.min(getMaxScrollY(), Math.max(0, Math.round(targetY)));

  if (prefersReduced) {
    if (scrollToTargetRafId != null) {
      cancelAnimationFrame(scrollToTargetRafId);
      scrollToTargetRafId = null;
    }
    setScrollY(end);
    return;
  }

  const start = getScrollY();
  const distance = end - start;
  if (Math.abs(distance) < 4) {
    setScrollY(end);
    return;
  }

  if (scrollToTargetRafId != null) {
    cancelAnimationFrame(scrollToTargetRafId);
    scrollToTargetRafId = null;
  }

  const duration = Math.min(900, Math.max(380, Math.abs(distance) * 0.45));
  const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();

  const step = (now) => {
    const elapsed = now - t0;
    const t = Math.min(1, elapsed / duration);
    const eased = easeOutCubic(t);
    const y = start + distance * eased;
    setScrollY(y);
    if (t < 1) {
      scrollToTargetRafId = requestAnimationFrame(step);
    } else {
      setScrollY(end);
      scrollToTargetRafId = null;
    }
  };

  scrollToTargetRafId = requestAnimationFrame(step);
}

/** Animated scroll to top. */
export function scrollToPageTopSmooth() {
  scrollToYOffsetSmooth(0);
}
